import { Card } from "@/components/ui/Card";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Edit } from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { fetchWithAuth } from "../../hooks/useAuth";
import { Order } from "./OrderDetailsPage";

export const EditContainer = ({ order }: { order: Order }) => {
  const queryClient = useQueryClient();

  const [invoiceNumber, setInvoiceNumber] = useState<string>(
    order.invoiceNumber || ""
  );
  const [documentId, setDocumentId] = useState<string>(
    order.metadata?.invoice?.documentId
      ? String(order.metadata.invoice.documentId)
      : ""
  );
  const [awbNumber, setAwbNumber] = useState<string>(order.awbNumber || "");

  const isSaveDisabled = useMemo(() => {
    return invoiceNumber.trim().length === 0;
  }, [invoiceNumber]);

  const { mutate: updateInvoice, isPending } = useMutation({
    mutationFn: async (payload: {
      invoiceNumber: string;
      documentId?: string;
    }) => {
      const response = await fetchWithAuth(
        `/api/admin/orders/${order.id}/invoice`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({} as any));
        throw new Error(
          error?.message || error?.error || "Eroare la actualizarea facturii"
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({
          queryKey: ["order", String(order.id)],
        }),
      ]);
      toast.success("Numărul facturii a fost actualizat");
    },
    onError: (error: any) => {
      toast.error(
        error instanceof Error
          ? error.message
          : "Eroare la actualizarea facturii"
      );
    },
  });

  const { mutate: updateAwb, isPending: isAwbPending } = useMutation({
    mutationFn: async (payload: { awbNumber: string }) => {
      const response = await fetchWithAuth(
        `/api/admin/orders/${order.id}/awb`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const error = await response.json().catch(() => ({} as any));
        throw new Error(
          error?.message || error?.error || "Eroare la actualizarea AWB"
        );
      }
      return response.json();
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({
          queryKey: ["order", String(order.id)],
        }),
      ]);
      toast.success("Numărul AWB a fost actualizat");
    },
    onError: (error: any) => {
      toast.error(
        error instanceof Error ? error.message : "Eroare la actualizarea AWB"
      );
    },
  });

  return (
    <Card className="p-4">
      <h2 className="font-semibold mt-4 mb-4 flex items-center gap-2">
        <Edit className="w-5 h-5 text-muted-foreground" />
        Editare
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-sm text-muted-foreground block mb-1">
            Număr factură
          </label>
          <Input
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="Introduceți numărul facturii"
          />
        </div>

        <div>
          <label className="text-sm text-muted-foreground block mb-1">
            Document ID
          </label>
          <Input
            value={documentId}
            onChange={(e) => setDocumentId(e.target.value)}
            placeholder="Introduceți documentId"
          />
        </div>

        <div className="md:col-span-3 flex gap-2">
          <Button
            onClick={() =>
              updateInvoice({
                invoiceNumber: invoiceNumber.trim(),
                documentId: documentId.trim() || undefined,
              })
            }
            disabled={isSaveDisabled || isPending}
            className="min-w-[120px]"
          >
            {isPending ? "Se salvează..." : "Salvează factură"}
          </Button>

          {order.invoiceNumber && (
            <div className="text-sm text-muted-foreground self-center">
              Curent:{" "}
              <span className="font-medium">
                {order.invoiceSeries ? `${order.invoiceSeries}/` : ""}
                {order.invoiceNumber}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-sm text-muted-foreground block mb-1">
              Număr AWB
            </label>
            <Input
              value={awbNumber}
              onChange={(e) => setAwbNumber(e.target.value)}
              placeholder="Introduceți numărul AWB"
            />
          </div>

          <div className="md:col-span-3 flex gap-2">
            <Button
              onClick={() => updateAwb({ awbNumber: awbNumber.trim() })}
              disabled={awbNumber.trim().length === 0 || isAwbPending}
              className="min-w-[120px]"
            >
              {isAwbPending ? "Se salvează..." : "Salvează AWB"}
            </Button>

            {order.awbNumber && (
              <div className="text-sm text-muted-foreground self-center">
                Curent: <span className="font-medium">{order.awbNumber}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
